import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAgent,
  VisitPlanState,
  ICasesData,
  IVisitPlanData,
  IVisitPlanCaseData,
  ICasesThinList
} from '../interface';
import { Option } from 'src/pages/CaseDetails/interfaces/EmiSchedule.type';
import { ViewType } from '../constants';
import { Address } from '../../CaseDetails/components/TopAddresses/interfaces';
import { IFilterDrawerSchema } from '@cp/src/components/FilterDrawer/types';

// Define the initial state
const initialState: VisitPlanState = {
  agencyFilterData: [],
  teamLeadFilterData: [],

  agentsLoading: false,
  agents: [] as IAgent[],

  activeTab: ViewType.VISIT_PLAN,

  allCases: { data: {}, thinList: [] },
  casesLoading: false,

  visitPlanLoading: false,
  todayVisitPlanCases: { data: {}, thinList: [] },
  previousVisitPlanCases: { data: {}, thinList: [] },
  tomorrowVisitPlanCases: { data: {}, thinList: [] },

  topAddresses: [],
  topAddressesLoading: false,

  selectedCaseId: null,
  caseFilterSchema: {},

  // Search functionality
  searchQuery: '',

  isDeleted: false,
  isAutoGenerateModalOpen: false
};

const visitPlanSlice = createSlice({
  name: 'visitPlan',
  initialState,
  reducers: {
    // Set filter data
    setAgencyFilterData: (state, action: PayloadAction<{ data: Array<Option> }>) => {
      const { data } = action.payload;
      state.agencyFilterData = data;
    },
    setTeamLeadFilterData: (state, action: PayloadAction<{ data: Array<Option> }>) => {
      const { data } = action.payload;
      state.teamLeadFilterData = data;
    },
    // Set agents loading state
    setAgentsLoading: (state, action: PayloadAction<boolean>) => {
      state.agentsLoading = action.payload;
    },
    // Set agents data
    setAgents: (state, action: PayloadAction<IAgent[]>) => {
      state.agents = action.payload;
    },
    // Set active tab
    setActiveTab: (state, action: PayloadAction<ViewType>) => {
      state.activeTab = action.payload;
    },
    // Set cases loading state
    setCasesLoading: (state, action: PayloadAction<boolean>) => {
      state.casesLoading = action.payload;
    },
    // Set cases data
    setCasesData: (
      state,
      action: PayloadAction<{ data: ICasesData; thinList: ICasesThinList[] }>
    ) => {
      state.allCases.data = action.payload?.data || {};
      state.allCases.thinList = action.payload?.thinList || [];
    },
    // Set visit plan cases data
    setTodayVisitPlanCases: (
      state,
      action: PayloadAction<{ data: IVisitPlanData; thinList: ICasesThinList[] }>
    ) => {
      state.todayVisitPlanCases.data = action.payload?.data || {};
      state.todayVisitPlanCases.thinList = action.payload?.thinList || [];
    },
    // Set previous visit plan cases data
    setPreviousVisitPlanCases: (
      state,
      action: PayloadAction<{ data: IVisitPlanData; thinList: ICasesThinList[] }>
    ) => {
      state.previousVisitPlanCases.data = action.payload?.data || {};
      state.previousVisitPlanCases.thinList = action.payload?.thinList || [];
    },
    // Set tomorrow visit plan cases data
    setTomorrowVisitPlanCases: (
      state,
      action: PayloadAction<{ data: IVisitPlanData; thinList: ICasesThinList[] }>
    ) => {
      state.tomorrowVisitPlanCases.data = action.payload?.data || {};
      state.tomorrowVisitPlanCases.thinList = action.payload?.thinList || [];
    },
    // Set visit plan loading state
    setVisitPlanLoading: (state, action: PayloadAction<boolean>) => {
      state.visitPlanLoading = action.payload;
    },
    // Set top addresses data
    setTopAddresses: (state, action: PayloadAction<Address[]>) => {
      state.topAddresses = action.payload;
    },
    // Set top addresses loading state
    setTopAddressesLoading: (state, action: PayloadAction<boolean>) => {
      state.topAddressesLoading = action.payload;
    },
    // Set selected case id
    setSelectedCaseId: (state, action: PayloadAction<string | null>) => {
      state.selectedCaseId = action.payload;
    },
    // Set case filter schema
    setCaseFilterSchema: (state, action: PayloadAction<Record<string, IFilterDrawerSchema>>) => {
      state.caseFilterSchema = action.payload;
    },
    // Add case to today's visit plan
    addCaseToVisitPlan: (
      state,
      action: PayloadAction<{ caseId: string; caseData: IVisitPlanCaseData }>
    ) => {
      const { caseId, caseData } = action.payload;

      state.todayVisitPlanCases.data[caseId] = caseData;

      // Remove existing entry if present, then add new one
      state.todayVisitPlanCases.thinList = state.todayVisitPlanCases?.thinList?.filter(
        item => item.caseReferenceId !== caseId
      );
      state.todayVisitPlanCases?.thinList?.push({
        caseReferenceId: caseId,
        addressId: caseData.address.addressId,
        location: caseData.address.location
      });
    },
    addCaseToTomorrowVisitPlan: (
      state,
      action: PayloadAction<{ caseId: string; caseData: IVisitPlanCaseData }>
    ) => {
      const { caseId, caseData } = action.payload;

      state.tomorrowVisitPlanCases.data[caseId] = caseData;

      // Remove existing entry if present, then add new one
      state.tomorrowVisitPlanCases.thinList = state.tomorrowVisitPlanCases?.thinList?.filter(
        item => item.caseReferenceId !== caseId
      );
      state.tomorrowVisitPlanCases?.thinList?.push({
        caseReferenceId: caseId,
        addressId: caseData.address.addressId,
        location: caseData.address.location
      });
    },
    // Remove case from today's visit plan
    removeCaseFromVisitPlan: (state, action: PayloadAction<string>) => {
      const caseId = action.payload;
      delete state.todayVisitPlanCases.data[caseId];
      state.todayVisitPlanCases.thinList = state.todayVisitPlanCases?.thinList?.filter(
        item => item.caseReferenceId !== caseId
      );
      state.isDeleted = true;
    },
    removeCaseFromTomorrowVisitPlan: (state, action: PayloadAction<string>) => {
      const caseId = action.payload;
      delete state.tomorrowVisitPlanCases.data[caseId];
      state.tomorrowVisitPlanCases.thinList = state.tomorrowVisitPlanCases?.thinList?.filter(
        item => item.caseReferenceId !== caseId
      );
      state.isDeleted = true;
    },
    resetVisitPlan: state => {
      state.topAddresses = [];
      state.selectedCaseId = null;
    },
    setIsDeleted: (state, action: PayloadAction<boolean>) => {
      state.isDeleted = action.payload;
    },
    // Set search query for frontend filtering
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setIsAutoGenerateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAutoGenerateModalOpen = action.payload;
    }
  }
});

export const {
  setAgencyFilterData,
  setTeamLeadFilterData,
  setAgentsLoading,
  setAgents,
  setActiveTab,
  setCasesLoading,
  setTodayVisitPlanCases,
  setVisitPlanLoading,
  setTomorrowVisitPlanCases,
  setPreviousVisitPlanCases,
  setCasesData,
  setTopAddresses,
  setTopAddressesLoading,
  setSelectedCaseId,
  setCaseFilterSchema,
  addCaseToVisitPlan,
  addCaseToTomorrowVisitPlan,
  removeCaseFromVisitPlan,
  removeCaseFromTomorrowVisitPlan,
  resetVisitPlan,
  setIsDeleted,
  setSearchQuery,
  setIsAutoGenerateModalOpen
} = visitPlanSlice.actions;

export default visitPlanSlice.reducer;
